// © Microsoft Corporation. All rights reserved.

import Popover, { PopoverProps } from "../../common/popover/Popover";
import { PopupConnector, UsersConnector } from "../../Social.props";
import { ProfileReportMenuTelemetryObjects, profileReportMenuTelemetryActionDispatchers, profileReportMenuTelemetryContracts } from "./ProfileReportMenu.telemetry";
import { SocialService, User } from "@msnews/social-data-service";
import { TelemetryEventMap, addOrUpdateTelemetryObject, dispatchTelemetryEvent, renderHybrid } from "../../Social.telemetry";

import AccessibilityHelpers from "../../utils/AccessibilityHelpers";
import { IRequestContext } from "@msnews/core";
import React from "react";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import { classNames } from "@microsoft/fast-web-utilities";
import { enums } from "../../utils/enums";
import { flag } from "../../utils/icons";

export interface ProfileReportMenuProps extends PopoverProps {
    managedClasses?: SocialClassNameContract;
    onReport?: (key: string, reportItemType: string) => void;
    t: SocialLocalizedStrings;
    mainTelemetryObject?: TelemetryObjectBase;
    theme?: string;
    popupConnector?: PopupConnector;
    id?: string;
    requestContext: IRequestContext;
    renderInPopover?: boolean;
    usersConnector?: UsersConnector;
    me?: User;
}

interface ProfileReportMenuState{
    containerWidth: number,
    containerHeight: number,
    containerOffsetLeft: number,
    selectedReportItemType: string;
    activeListId: number;
}

export default class ProfileReportMenu extends React.Component<ProfileReportMenuProps, ProfileReportMenuState> {
    containerRef: HTMLDivElement;
    listRefs: Array<HTMLUListElement>;
    telemetryObjects: ProfileReportMenuTelemetryObjects;
    isLoaded: boolean;

    constructor(props) {
        super(props);
        this.state = {
            containerWidth: null,
            containerHeight: null,
            containerOffsetLeft: null,
            selectedReportItemType: null,
            activeListId: 0
        };

        this.containerRef = null;
        this.listRefs = [];
    }

    componentDidMount() {
        this.showNextList(this.state.activeListId);
        AccessibilityHelpers.attachAccessibilityListeners(document);
        if (!this.props.me) { this.props.usersConnector.fetchMe(); }

        this.loadedHandler();
    }

    componentDidUpdate() {
        this.loadedHandler();
    }

    selectReportItemType(e, reportItemType, telemetryObject) {
        const nextListId = 1;

        this.setState({
            selectedReportItemType: reportItemType,
            activeListId: nextListId
        });

        this.showNextList(nextListId);
        profileReportMenuTelemetryActionDispatchers.reportTypeClicked(e.currentTarget, telemetryObject);
    }

    showNextList(activeListId) {
        this.setState({
            containerWidth: this.listRefs[activeListId].offsetWidth,
            containerHeight: this.listRefs[activeListId].offsetHeight,
            containerOffsetLeft: -1*this.listRefs[activeListId].offsetLeft
        });
        const firstChild = this.listRefs[activeListId].children[1] as HTMLElement;
        firstChild.focus();
    }

    render() {
        if (!this.props.me) { return null; }

        this.generateTelemetryObjects();

        return this.props.renderInPopover ? this.renderDefault() : this.renderMobile();
    }

    renderDefault() {
        const { managedClasses, t, mainTelemetryObject, responsiveClass } = this.props;
        const { containerWidth, containerHeight, containerOffsetLeft, selectedReportItemType } = this.state;

        return (
            <Popover {...{...this.props, className: managedClasses.profileReportMenuPopover, hasFocusLock: true}}>
                <div
                    className={classNames(managedClasses.profileReportMenuContainer, responsiveClass)}
                    style={{height: containerHeight, width: containerWidth}}
                    {...renderHybrid(mainTelemetryObject, true, (el) => { this.containerRef = el; })}
                >
                    <div className="cwt-sliding-container" style={{left: containerOffsetLeft}}>
                        <ul className="cwt-report-list" role="listbox" aria-label={ t.ProfileReportUserTitle } ref={(instance: HTMLUListElement)=>{ this.listRefs[0] = instance; }}>
                            <i className="cwt-icon cwt-menu-header">
                                {flag}
                                <span className="cwt-icon-label">{ t.ProfileReportUserTitle }</span>
                            </i>
                            { Object.keys(enums.profileReportItemMap).map(key => {
                                return <li className="cwt-link" role="option" key={key} onClick={(e) => this.selectReportItemType(e, key, this.telemetryObjects && this.telemetryObjects[`reportUpperType-${key}`])} {...renderHybrid(this.telemetryObjects && this.telemetryObjects[`reportUpperType-${key}`], false)}>
                                    {t[enums.profileReportItemMap[key]]}
                                </li>;
                            })
                            }
                        </ul>
                        <ul
                            className="cwt-report-list"
                            role="listbox"
                            aria-labelledby="cwt-menu-header-username"
                            {...renderHybrid(this.telemetryObjects && this.telemetryObjects.reportUserMenuTypes, true, (el) => this.listRefs[1] = el)}
                        >
                            <i className="cwt-icon cwt-menu-header" id="cwt-menu-header-username">
                                {flag}
                                <span className="cwt-icon-label">{ t[enums.profileReportItemMap[selectedReportItemType]] }</span>
                            </i>
                            { Object.keys(selectedReportItemType == "UserName" ? enums.userNameReportMap : enums.userPictureReportMap).map(key => {
                                return <li className="cwt-access-li" role="option" key={key} onClick={(e) => this.reportHandler(e, key, this.telemetryObjects && this.telemetryObjects[`reportType-${key}`])} {...renderHybrid(this.telemetryObjects && this.telemetryObjects[`reportType-${key}`], false)}>
                                    {t[selectedReportItemType == "UserName" ? enums.userNameReportMap[key] : enums.userPictureReportMap[key]]}
                                </li>;
                            })
                            }
                        </ul>
                    </div>
                </div>
            </Popover>
        );
    }

    renderMobile() {
        const { managedClasses, t, mainTelemetryObject, responsiveClass } = this.props;
        const { containerOffsetLeft, containerHeight, selectedReportItemType } = this.state;

        return (
            <div
                className={classNames(
                    managedClasses.profileReportMenuContainer,
                    managedClasses.profileReportMenuSuperAppContainer,
                    responsiveClass
                )}
                style={{height: containerHeight}}
                {...renderHybrid(mainTelemetryObject, true, (el) => { this.containerRef = el; })}
            >
                <div style={{left: containerOffsetLeft}}>
                    <ul className="cwt-report-list" role="listbox" aria-label={ t.ProfileReportUserTitle } ref={(instance: HTMLUListElement)=>{ this.listRefs[0] = instance; }}>
                        <div className="spacer"></div>
                        { Object.keys(enums.profileReportItemMap).map(key => {
                            return <li className="cwt-link" role="option" key={key} onClick={(e) => this.selectReportItemType(e, key, this.telemetryObjects && this.telemetryObjects[`reportUpperType-${key}`])} {...renderHybrid(this.telemetryObjects && this.telemetryObjects[`reportUpperType-${key}`], false)}>
                                <i className="cwt-icon">
                                    {flag}
                                    <span className="cwt-icon-label">{t[enums.profileReportItemMap[key]]}</span>
                                </i>
                            </li>;
                        })
                        }
                    </ul>
                    <ul
                        className="cwt-report-list cwt-report-list-final"
                        role="listbox"
                        aria-labelledby="cwt-menu-header-username"
                        ref={(instance: HTMLUListElement)=>{ this.listRefs[1] = instance; }}
                        {...renderHybrid(this.telemetryObjects && this.telemetryObjects.reportUserMenuTypes, true, (el) => this.listRefs[1] = el)}
                    >
                        <i className="cwt-icon cwt-menu-header" id="cwt-menu-header-username">
                            <span className="cwt-icon-label">{ t[enums.profileReportItemMap[selectedReportItemType]] }</span>
                        </i>
                        { Object.keys(selectedReportItemType == "UserName" ? enums.userNameReportMap : enums.userPictureReportMap).map(key => {
                            return <li className="cwt-access-li" role="option" key={key} onClick={e => this.reportHandler(e, key, this.telemetryObjects && this.telemetryObjects[`reportType-${key}`])} {...renderHybrid(this.telemetryObjects && this.telemetryObjects[`reportType-${key}`], false)}>
                                {t[selectedReportItemType == "UserName" ? enums.userNameReportMap[key] : enums.userPictureReportMap[key]]}
                            </li>;
                        })
                        }
                    </ul>
                </div>
            </div>
        );
    }

    generateTelemetryObjects() {
        const { mainTelemetryObject } = this.props;
        const commonContent = mainTelemetryObject.contract.content;
        this.telemetryObjects = {
            reportUserMenuTypes: addOrUpdateTelemetryObject(mainTelemetryObject, {...profileReportMenuTelemetryContracts.reportUserMenuTypes}, {...commonContent}, this.telemetryObjects && this.telemetryObjects.reportUserMenuTypes)
        };
        Object.keys(enums.profileReportItemMap).map(key => {
            this.telemetryObjects[`reportUpperType-${key}`] = addOrUpdateTelemetryObject(mainTelemetryObject, {...mainTelemetryObject.contract}, {...commonContent, headline: key}, this.telemetryObjects && this.telemetryObjects[`reportUpperType-${key}`]);
        });
        Object.keys(enums.userNameReportMap).map(key => {
            this.telemetryObjects[`reportType-${key}`] = addOrUpdateTelemetryObject(mainTelemetryObject, {...profileReportMenuTelemetryContracts.reportUserMenuTypes}, {...commonContent, headline: key}, this.telemetryObjects && this.telemetryObjects[`reportType-${key}`]);
        });
        Object.keys(enums.userPictureReportMap).map(key => {
            this.telemetryObjects[`reportType-${key}`] = addOrUpdateTelemetryObject(mainTelemetryObject, {...profileReportMenuTelemetryContracts.reportUserMenuTypes}, {...commonContent, headline: key}, this.telemetryObjects && this.telemetryObjects[`reportType-${key}`]);
        });
    }

    reportHandler(e, reportType, telemetryObject) {
        profileReportMenuTelemetryActionDispatchers.reportTypeClicked(e.currentTarget, telemetryObject);
        const reportValue = enums.commentReportMap[reportType];
        if (reportValue == enums.commentReportMap.Copyright) {
            this.props.popupConnector.closePopup({disableBridge: true});
            if (this.props.me.state == enums.userStateType.anonymous) {
                this.props.popupConnector.openPopup("multiLoginPage");
            } else {
                this.props.popupConnector.openPopup(
                    reportValue == enums.commentReportMap.Copyright ? "copyrightform" : "",
                    {id: this.props.id, fullScreen: false, reportItemType: this.state.selectedReportItemType, onReport: this.props.onReport});
            }
        } else {
            SocialService.reportUser(this.props.id, this.state.selectedReportItemType, reportType);
            this.props.renderInPopover && this.props.onReport && this.props.onReport(reportType, this.state.selectedReportItemType);
            this.props.popupConnector.closePopup();
        }
    }

    loadedHandler() {
        if (this.isLoaded) { return; }

        if (this.containerRef) {
            dispatchTelemetryEvent({...TelemetryEventMap.genericLoaded, sendPeregrine: true}, this.containerRef);
            this.isLoaded = true;
        }
    }
}